import { CapagTypes } from 'store/modules/bidding_store/types';

export enum BiddingSummaryTypes {
  OPEN_BIDDING_SUMMARY = '@bidding_summary/OPEN_BIDDING_SUMMARY',
  CLOSE_BIDDING_SUMMARY = '@bidding_summary/CLOSE_BIDDING_SUMMARY',
  FOCUS_BIDDING_SUMMARY = '@bidding_summary/FOCUS_BIDDING_SUMMARY',
  CLEAR_CURRENT_BIDDING_SUMMARY = '@bidding_summary/CLEAR_CURRENT_BIDDING_SUMMARY',
  FETCH_BIDDING_SUMMARY_REQUEST = '@bidding_summary/FETCH_BIDDING_SUMMARY_REQUEST',
  FETCH_BIDDING_SUMMARY_SUCCESS = '@bidding_summary/FETCH_BIDDING_SUMMARY_SUCCESS',
  FETCH_BIDDING_SUMMARY_FAILURE = '@bidding_summary/FETCH_BIDDING_SUMMARY_FAILURE',
  FETCH_BIDDER_CONSIDERATION_REQUEST = '@bidding_summary/FETCH_BIDDER_CONSIDERATION_REQUEST',
  FETCH_BIDDER_CONSIDERATION_SUCCESS = '@bidding_summary/FETCH_BIDDER_CONSIDERATION_SUCCESS',
  FETCH_BIDDER_CONSIDERATION_FAILURE = '@bidding_summary/FETCH_BIDDER_CONSIDERATION_FAILURE',
  SAVE_BIDDER_CONSIDERATION_REQUEST = '@bidding_summary/SAVE_BIDDER_CONSIDERATION_REQUEST',
  SAVE_BIDDER_CONSIDERATION_SUCCESS = '@bidding_summary/SAVE_BIDDER_CONSIDERATION_SUCCESS',
  SAVE_BIDDER_CONSIDERATION_FAILURE = '@bidding_summary/SAVE_BIDDER_CONSIDERATION_FAILURE',
  DELETE_BIDDER_CONSIDERATION_REQUEST = '@bidding_summary/DELETE_BIDDER_CONSIDERATION_REQUEST',
  DELETE_BIDDER_CONSIDERATION_SUCCESS = '@bidding_summary/DELETE_BIDDER_CONSIDERATION_SUCCESS',
  DELETE_BIDDER_CONSIDERATION_FAILURE = '@bidding_summary/DELETE_BIDDER_CONSIDERATION_FAILURE',
  FETCH_BIDDER_CONSIDERATION_TEAM_REQUEST = '@bidding_summary/FETCH_BIDDER_CONSIDERATION_TEAM_REQUEST',
  FETCH_BIDDER_CONSIDERATION_TEAM_SUCCESS = '@bidding_summary/FETCH_BIDDER_CONSIDERATION_TEAM_SUCCESS',
  FETCH_BIDDER_CONSIDERATION_TEAM_FAILURE = '@bidding_summary/FETCH_BIDDER_CONSIDERATION_TEAM_FAILURE',
  CLEAR_BIDDING_SUMMARY_DATA = '@bidding_summary/CLEAR_BIDDING_SUMMARY_DATA',
}

export interface BiddingSummaryEditalFile {
  url: string;
  filename: string;
}

export interface BiddingSummaryEditalDownload {
  biddingStatusEdital: number;
  biddingFiles: BiddingSummaryEditalFile[];
  situation?: string;
  biddingValidade?: string | null;
}

export interface BiddingSummaryCapagInfo {
  temCapag?: boolean;
  capag?: CapagTypes;
}

export interface BiddingSummaryTabState {
  modalOpened: boolean;
  currentBiddingId: number;
  editalDownload?: BiddingSummaryEditalDownload;
  capagInfo?: BiddingSummaryCapagInfo;
}
export interface BiddingSummaryStates {
  [key: string]: BiddingSummaryTabState;
}

export interface ResumoItensLicitados {
  total_itens: number;
  categorias_principais: string[];
  descricao_geral: string;
  observacoes: string;
}

/** Prazos no formato novo do resumo (objeto aninhado). */
export interface PrazosImportantes {
  tipo_fornecimento?: string;
  prazo_envio_doc_complementar?: string;
  prazo_envio_doc_original?: string;
  prazo_contratual_ou_arp?: string;
  limite_envio_propostas?: string;
  prazo_manifestacao_recurso?: string;
  prazo_recursos_contrarrazoes?: string;
  prazo_esclarecimentos?: string;
  limite_impugnacao?: string;
  prazo_vigencia_contrato_ata?: string;
}

export interface CriteriosPropostaJulgamento {
  validade_proposta?: string;
  exigencias_proposta_comercial?: string;
  criterios_desempate?: string;
}

export interface AnaliseCritica {
  conflito_objeto_minuta?: string;
  conflito_prazo_vigencia_arp?: string;
  conflito_prazos_entrega?: string;
  permite_subcontratacao?: string;
  previsao_reajuste_repactuacao?: string;
  permite_renovacao_contrato?: string;
  estabelece_condicoes_pagamento?: string;
}

export interface PossibilidadeQuestionamentoResumo {
  tema?: string;
  tipo?: string;
  existe_irregularidade?: boolean;
  justificativa?: string;
  trecho_edital?: string;
}

export interface DocumentRequirementItem {
  documento?: string;
  trecho_edital?: string;
  confianca?: 'alta' | 'baixa';
}

/** Exigências por categoria; a chave do objeto é a label exibida no badge (ex.: `Saúde` → lista de itens). */
export type ExigenciasEspecificas = Record<string, string[]>;

/**
 * Garantias no formato estruturado do resumo.
 * `seguro_garantia` vem da API como indicador geral; não é exibido na UI.
 */
export interface GarantiasResumo {
  garantia_proposta?: boolean;
  garantia_contrato?: boolean;
  garantia_adicional?: boolean;
  garantia_retomada?: boolean;
  seguro_garantia?: boolean;
  justificativa_proposta?: string;
  justificativa_contrato?: string;
  justificativa_adicional?: string;
  justificativa_retomada?: string;
  justificativa_seguro_garantia?: string;
}

/**
 * Payload de `summary`: formato legado e novo convivem (campos opcionais conforme origem).
 */
export interface BiddingSummaryData {
  numero_licitacao: string;
  objeto: string;
  data_sessao_publica: string;
  horario_sessao_publica: string;
  /** Legado: um e-mail. Novo: preferir `emails`. */
  email_orgao?: string;
  emails?: string[];
  telefone_orgao?: string;
  modo_disputa: string;
  valor_estimado_licitacao: number;
  valor_estimado_observacao?: string;
  /** Legado: garantia única. Novo: `garantia_da_proposta` / `garantia_do_contrato` ou `garantias`. */
  garantia?: string;
  garantia_da_proposta?: string;
  garantia_do_contrato?: string;
  /** Formato estruturado (API nova): acordeão dedicado e justificativas. */
  garantias?: GarantiasResumo;
  prazo_de_entrega: string;
  data_entrega_execucao?: string;
  local_entrega_execucao?: string;
  orgao_responsavel: string;
  orgao_endereco?: string;
  criterio_de_julgamento: string;
  margem_de_preferencia: string;
  exigencia_de_visita_tecnica: string;
  preferencia_de_me_epp: string;
  uasg?: string;
  portal_realizacao?: string;
  tipo_contratacao?: string;
  agente_contratacao?: string;
  restricoes_regionalidade?: string;
  exigencia_amostra?: string;
  exigencia_prova_conceito?: string;
  programa_integridade?: string;
  intervalo_minimo?: string;
  legislacao?: string[];
  prazos_importantes?: PrazosImportantes;
  criterios_proposta_julgamento?: CriteriosPropostaJulgamento;
  condicoes_pagamento?: string;
  penalidades_multas?: string[];
  anexos_declaracoes?: (string | DocumentRequirementItem)[];
  analise_critica?: AnaliseCritica;
  atestado_capacidade_tecnica?: (string | DocumentRequirementItem)[];
  possibilidades_questionamentos?: (string | PossibilidadeQuestionamentoResumo)[];
  resumo_itens_licitados: ResumoItensLicitados;
  outras_informacoes_relevantes: string[];
  documentos_habilitacao: (string | DocumentRequirementItem)[];
  exigencias_especificas?: ExigenciasEspecificas;
  riscos_identificados: string[];
  oportunidades_identificadas: string[];
  /** ISO-8601 com offset (ex.: resposta da API de resumo). */
  resumo_criado_em?: string;
}

export interface BiddingSummaryDataTypes {
  summary: BiddingSummaryData;
}

export interface BiddingSummaryBidderConsideration {
  id: number;
  bidding_id: number;
  client_id: number;
  contact_client_id: number;
  consideration: string;
  created_at: string;
  updated_at: string;
}

export interface BidderConsiderationTeamRow {
  id: number;
  bidding_id: number;
  client_id: number;
  contact_client_id: number;
  contact_name: string;
  consideration: string;
  updated_at: string;
}

export interface BiddingSummaryDataStates {
  current_bidding_summary: BiddingSummaryStates;
  bidding_summary_data: { [key: string]: BiddingSummaryDataTypes };
  bidder_consideration_data: { [key: string]: BiddingSummaryBidderConsideration | null };
  bidder_consideration_team_data: { [key: string]: BidderConsiderationTeamRow[] };
}

export interface DefaultRequestTypes {
  loading: boolean;
  loaded: boolean;
  error: boolean;
}

export interface RequestState {
  fetch_bidding_summary: { [key: string]: DefaultRequestTypes };
  fetch_bidder_consideration: { [key: string]: DefaultRequestTypes };
  fetch_bidder_consideration_team: { [key: string]: DefaultRequestTypes };
  save_bidder_consideration: { [key: string]: DefaultRequestTypes };
  delete_bidder_consideration: { [key: string]: DefaultRequestTypes };
}

export interface BiddingSummaryState {
  readonly bidding_summary: BiddingSummaryDataStates;
  readonly request: RequestState;
}
